import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
// import { useNavigate } from 'react-router-dom';
import { SparkCreative, SparkIteration, SparkCreativeType, SparkTagDimensionSelection, SparkFormDefinition, SparkFlowStep, SparkJob } from '../../types/sparkTypes';
import { useAtlasStore, AtlasState } from '../../../../store/atlasStore';
import { atlasClient } from '../../../../api/atlasClient';
import styles from '../../styles/CreativeEditor.module.css';
import IterationView from '../editor/IterationView';
import ViewerHeader from '../editor/ViewerHeader';
import HeroSection from '../editor/HeroSection';
import FormNavSidebar from '../editor/FormNavSidebar';
import VersionHistoryPanel from '../editor/VersionHistoryPanel';
import { toast } from 'react-toastify';
import { ConfirmationModal } from './ConfirmationModal';
import { getNewestUnseenIteration } from '../../utils/timeUtils';
import { fetchJSON } from '../../../../api/fetchHelpers';
import { authenticatedFetch, parseJsonResponse } from '../../../../api/apiClient';
import useSparkTagOptions from '../../hooks/useSparkTagOptions';
import { TagContextSelectorConstraints } from '../tags/TagContextSelector';
import { adaptLibraryItemToIteration } from '../../utils/libraryAdapter';
import { useAuth } from '../../../../contexts/AuthContext';
import { useSparkActions, ResolvedSparkAction } from '../../hooks/useSparkActions';
import SparkActionModal from './SparkActionModal';
import { ActivityPanel } from '../ActivityPanel';
import { JobDetailModal } from './JobDetailModal';
import EditorJobIndicator from '../editor/EditorJobIndicator';
import { getJobResultNavigation, JobNavigationDeps } from '../../utils/jobNavigation';
import { buildSparkFormDefinitions, formatSparkFormLabel, resolveSparkFormLabelFromOverlay } from '../../utils/forms';

// MUI Icons
import CircularProgress from '@mui/material/CircularProgress';
import ErrorIcon from '@mui/icons-material/Error';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

// Stable empty collections to prevent unnecessary re-renders
const EMPTY_TAG_PATTERN: SparkTagDimensionSelection[] = [];
const EMPTY_ELEMENT_MAP = new Map<string, Set<string>>();

interface CreativeEditorProps {
  isOpen: boolean;
  onClose: () => void;
  creativeFqn?: string; // optional when editorMode='item'
  projectFqn?: string;  // optional when editorMode='item'
  projectTagPattern?: SparkTagDimensionSelection[] | null;
  creativeType?: SparkCreativeType;
  onDataRefreshed?: () => void; // Optional callback when editor refreshes data
  isProjectClosed?: boolean;
  // Item mode
  editorMode?: 'iteration' | 'item';
  itemFqn?: string;
  // Initial tab when restoring state (e.g., returning from citation drill-down)
  initialTab?: 'details' | 'attachments' | 'deepsights';
  initialIterationFqn?: string;
  initialFeedbackId?: string;
  navigateToStep?: JobNavigationDeps['navigateToStep'];
  openCreativeEditor?: JobNavigationDeps['openCreativeEditor'];
  resolveIteration?: JobNavigationDeps['resolveIteration'];
  resolveFeedbackIdForJob?: JobNavigationDeps['resolveFeedbackIdForJob'];
  scrollToFeedback?: JobNavigationDeps['scrollToFeedback'];
}

export const CreativeEditor: React.FC<CreativeEditorProps> = ({
  isOpen,
  onClose,
  creativeFqn = '',
  projectFqn = '',
  projectTagPattern,
  creativeType,
  onDataRefreshed, // Use this prop
  isProjectClosed,
  editorMode = 'iteration',
  itemFqn,
  initialTab,
  initialIterationFqn,
  initialFeedbackId,
  navigateToStep,
  openCreativeEditor,
  resolveIteration,
  resolveFeedbackIdForJob,
  scrollToFeedback,
}) => {
  const isItemMode = editorMode === 'item';
  // State
  const [creative, setCreative] = useState<SparkCreative | null>(null);
  const [iterations, setIterations] = useState<SparkIteration[]>([]);
  const [activeIterationFqn, setActiveIterationFqn] = useState<string | null>(null);
  const [isLoadingCreative, setIsLoadingCreative] = useState(false);
  const [isLoadingIterations, setIsLoadingIterations] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [displayedIterationFqn, setDisplayedIterationFqn] = useState<string | null>(initialIterationFqn || null);

  // Use rAF to apply the open class one frame after mount for smooth fade-in
  const [animateOpen, setAnimateOpen] = useState(false);
  const openRafRef = useRef<number | null>(null);

  // Delete confirmation modal state
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [iterationToDeleteFqn, setIterationToDeleteFqn] = useState<string | null>(null);
  const [isDeletingIteration, setIsDeletingIteration] = useState(false);
  const [isDeleteCreativeOpen, setIsDeleteCreativeOpen] = useState(false);
  const [isDeletingCreative, setIsDeletingCreative] = useState(false);

  // Add state for the creative being displayed (initially from prop) and unseen list
  const [displayedCreativeFqn, setDisplayedCreativeFqn] = useState<string>(creativeFqn);
  const [unseenCreativeFqns, setUnseenCreativeFqns] = useState<string[]>([]);
  // Prefer live project state from store to ensure UI reflects latest closed state
  const storeProjectClosed = useAtlasStore((state: AtlasState) => state.currentSparkProject?.is_closed);
  const storeProjectUserIsAdmin = useAtlasStore((state: AtlasState) => state.currentSparkProject?.current_user_is_admin);
  const isClosed = (storeProjectClosed ?? isProjectClosed) ? true : false;
  const isPublished = !!creative?.is_published;
  const isReadOnly = isClosed || isPublished;
  const creativeTypeAllowsPublish = !!creativeType?.library;
  // Legacy publish/unpublish actions removed; publish flow now creates Library Items
  const createLibraryItemFromIteration = useAtlasStore((state: AtlasState) => state.createLibraryItemFromIteration);
  // const navigate = useNavigate(); // no direct navigation on publish to library

  // Subscribe to project details to watch completed job count
  const completedJobCount = useAtlasStore((state: AtlasState) => state.currentSparkProject?._completed_job_count);
  // Track previous completed job count
  const prevCompletedCountRef = useRef<number | null>(null);

  // Controlled tab state for IterationView
  const [controlledActiveTab, setControlledActiveTab] = useState<'details' | 'attachments' | 'deepsights'>(initialTab || 'details');
  const [scrollToFeedbackTimestamp, setScrollToFeedbackTimestamp] = useState<string | null>(null);
  const [scrollToFeedbackId, setScrollToFeedbackId] = useState<string | null>(initialFeedbackId || null);
  const [isActivityPanelOpen, setIsActivityPanelOpen] = useState(false);
  const [activityPanelScope, setActivityPanelScope] = useState<{ creativeFqn: string; iterationFqns: string[] } | null>(null);
  const [selectedJobFqn, setSelectedJobFqn] = useState<string | null>(null);

  // Per-tab scroll position save/restore (shared viewerContentScroll container)
  const savedScrollPositions = useRef<Record<string, number>>({});
  const prevTabRef = useRef(controlledActiveTab);
  const pendingSectionScrollRef = useRef<string | null>(null);

  // New layout state
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [locallySeenAt, setLocallySeenAt] = useState<number | null>(null);
  const isDeepsightsTabActive = controlledActiveTab === 'deepsights';

  // Presentation state (lifted from IterationView)
  const presentHandlersRef = useRef<{ togglePresent: () => void; downloadPptx: () => void } | null>(null);
  const [presentState, setPresentState] = useState<{
    showPresent: boolean;
    isPresentActive: boolean;
    isGeneratingSlides: boolean;
    slideCountdown: number | null;
    showDownloadPptx: boolean;
    isDownloadingPptx: boolean;
  }>({ showPresent: false, isPresentActive: false, isGeneratingSlides: false, slideCountdown: null, showDownloadPptx: false, isDownloadingPptx: false });

  // Scroll-spy state (mirrors UnifiedViewer pattern)
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const deepsightsSheetHostRef = useRef<HTMLDivElement>(null);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Get relevant store actions
  const setSparkUserInteractionBlockingRefresh = useAtlasStore(
    (state: AtlasState) => state.setSparkUserInteractionBlockingRefresh
  );
  const deleteSparkCreatives = useAtlasStore((state: AtlasState) => state.deleteSparkCreatives);
  const projectTagsFromStore = useAtlasStore((state: AtlasState) => state.currentSparkProject?.tag_pattern);
  const updateSparkCreative = useAtlasStore((state: AtlasState) => state.updateSparkCreative);
  const cacheSparkIterations = useAtlasStore((state: AtlasState) => state.cacheSparkIterations);
  const tenantUsers = useAtlasStore((state: AtlasState) => state.tenantUsers);
  const fetchTenantUsers = useAtlasStore((state: AtlasState) => state.fetchTenantUsers);
  const markSparkJobAsSeen = useAtlasStore((state: AtlasState) => state.markSparkJobAsSeen);

  // Store subscriptions for form navigation (iteration mode)
  const formDefinitionsByCreative = useAtlasStore((state: AtlasState) => state.sparkFormDefinitions);
  const storeCreativeTypes = useAtlasStore((state: AtlasState) => state.sparkCreativeTypes);
  // Compute orderedFormIds and formatFormLabel for iteration mode sidebar
  const iterationFormsMap = useMemo<Record<string, any>>(() => {
    if (isItemMode) return {};
    const defs = formDefinitionsByCreative[creative?.fqn ?? ''];
    return (defs as Record<string, any>) || {};
  }, [isItemMode, formDefinitionsByCreative, creative?.fqn]);

  const iterationCreativeType = useMemo(() => {
    if (isItemMode) return creativeType;
    return storeCreativeTypes?.find((type: SparkCreativeType) => type.fqn === creative?._creative_type_fqn) || creativeType;
  }, [isItemMode, storeCreativeTypes, creative?._creative_type_fqn, creativeType]);

  const iterationOrderedFormIds = useMemo<string[]>(() => {
    if (isItemMode) return [];
    const ids = Object.keys(iterationFormsMap);
    const creativeOverlay = (creative as any)?.forms_overlay as { id: string }[] | undefined;
    if (creativeOverlay && creativeOverlay.length) {
      const order = creativeOverlay.map(f => f.id).filter(id => ids.includes(id));
      if (order.length) return order;
    }
    const typeOverlay = iterationCreativeType?.forms_overlay as { id: string }[] | undefined;
    if (typeOverlay && typeOverlay.length) {
      const order = typeOverlay.map(f => f.id).filter(id => ids.includes(id));
      if (order.length) return order;
    }
    return ids;
  }, [isItemMode, iterationFormsMap, creative, iterationCreativeType]);

  // Spark actions for floating AI bar (iteration mode only)
  const currentSparkFlow = useAtlasStore((state: AtlasState) => state.currentSparkFlow);
  const sparkActionStepIndex = useMemo(() => {
    if (isItemMode || !currentSparkFlow?.steps || !creative?._creative_type_fqn) return -1;
    const idx = currentSparkFlow.steps.findIndex((s: SparkFlowStep) => s.creative_type_fqn === creative._creative_type_fqn);
    return idx >= 0 ? 2 + idx : -1;
  }, [isItemMode, currentSparkFlow, creative?._creative_type_fqn]);
  const sparkActionSelectedCreatives = useMemo(() => creative ? [creative] : [], [creative]);
  const { actions: sidebarActions } = useSparkActions(
    currentSparkFlow,
    sparkActionStepIndex,
    sparkActionSelectedCreatives
  );

  // Spark action modal state for floating AI bar actions
  const [sidebarCurrentAction, setSidebarCurrentAction] = useState<ResolvedSparkAction | null>(null);
  const [sidebarActionArgs, setSidebarActionArgs] = useState<Record<string, any>>({});
  const [isSidebarActionModalOpen, setIsSidebarActionModalOpen] = useState(false);

  const handleSidebarActionSelect = useCallback((action: ResolvedSparkAction) => {
    if (isReadOnly) return;
    const displayedIteration = displayedIterationFqn
      ? iterations.find(iter => iter.fqn === displayedIterationFqn)
      : null;
    const baseArgs: Record<string, any> = {
      input_iteration_fqns: displayedIteration ? [displayedIteration.fqn] : [],
      input_creative_fqns: creative ? [creative.fqn] : [],
    };
    if (action.output_type_fqn) baseArgs.output_type_fqn = action.output_type_fqn;
    setSidebarCurrentAction(action);
    setSidebarActionArgs(baseArgs);
    setIsSidebarActionModalOpen(true);
  }, [isReadOnly, displayedIterationFqn, iterations, creative]);

  // Build action items for the floating AI bar
  const sidebarActionItems = useMemo(() => {
    if (isItemMode || isReadOnly || sidebarActions.length === 0) return undefined;
    return sidebarActions.map((action) => ({
      label: action.formatted_label,
      logoUrl: action.logo_url,
      description: action.formatted_description,
      onClick: () => handleSidebarActionSelect(action),
    }));
  }, [isItemMode, isReadOnly, sidebarActions, handleSidebarActionSelect]);

  const [tagError, setTagError] = useState<string | null>(null);
  // Track pending tag persist promise so we can wait for it before closing
  const pendingTagPersistRef = useRef<Promise<void> | null>(null);
  const [publicationMode, setPublicationMode] = useState<'publish' | 'unpublish' | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);

  // Ensure tenant user directory is populated for display names in item mode
  useEffect(() => {
    if (editorMode === 'item' && isOpen) {
      if (!tenantUsers || tenantUsers.length === 0) {
        try { fetchTenantUsers(false); } catch { /* ignore */ }
      }
    }
  }, [editorMode, isOpen, tenantUsers, fetchTenantUsers]);

  // Creative name editing state (header)
  const [isEditingCreativeName, setIsEditingCreativeName] = useState(false);
  const [editCreativeName, setEditCreativeName] = useState('');
  const [isSavingCreativeName, setIsSavingCreativeName] = useState(false);
  const creativeNameInputRef = useRef<HTMLInputElement>(null);

  // Creative description editing state (header subheader)
  const [isEditingCreativeDescription, setIsEditingCreativeDescription] = useState(false);
  const [editCreativeDescription, setEditCreativeDescription] = useState('');
  const [isSavingCreativeDescription, setIsSavingCreativeDescription] = useState(false);
  const creativeDescriptionInputRef = useRef<HTMLInputElement>(null);

  const effectiveProjectTagPattern = projectTagPattern ?? projectTagsFromStore;

  // Union of project-allowed dimensions and creative-type schema dimensions
  const dimensionFqns = useMemo(() => {
    const projectDims = effectiveProjectTagPattern ? new Set(effectiveProjectTagPattern.map(entry => entry.dimension_fqn)) : undefined;
    const typeDims = creativeType?.creative_tag_schema_fqn ? new Set(creativeType.creative_tag_schema_fqn.map(entry => entry.dimension_fqn)) : undefined;
    if (projectDims && typeDims) {
      const union = new Set<string>(projectDims);
      typeDims.forEach((fqn) => union.add(fqn));
      return union;
    }
    return projectDims || typeDims || undefined;
  }, [effectiveProjectTagPattern, creativeType?.creative_tag_schema_fqn]);

  const publishBlockedReason = useMemo(() => {
    if (isPublished) {
      return undefined;
    }
    if (!creativeTypeAllowsPublish) {
      return 'Publishing is disabled for this creative type.';
    }
    if (!creative) {
      return 'Creative still loading';
    }
    if (isClosed) {
      return 'Project is closed.';
    }
    if (!storeProjectUserIsAdmin) {
      return 'Insufficient permissions to publish.';
    }
    if (!creative._active_iteration_fqn) {
      return 'Publishing requires an active iteration.';
    }
    if ((creative._active_jobs ?? 0) > 0) {
      return 'Publishing waits for in-flight jobs to finish.';
    }
    return undefined;
  }, [creative, creativeTypeAllowsPublish, isClosed, isPublished, storeProjectUserIsAdmin]);

  const canPublish = creativeTypeAllowsPublish && !isPublished && !publishBlockedReason && !!storeProjectUserIsAdmin;
  const canUnpublish = creativeTypeAllowsPublish && !!storeProjectUserIsAdmin && isPublished && !isClosed;

  const handleRequestPublish = useCallback(() => {
    if (!canPublish) {
      if (publishBlockedReason) {
        toast.info(publishBlockedReason);
      }
      return;
    }
    setPublicationMode('publish');
  }, [canPublish, publishBlockedReason]);

  // Unpublish flow
  const handleRequestUnpublish = useCallback(() => {
    if (!canUnpublish) return;
    setPublicationMode('unpublish');
  }, [canUnpublish]);

  const handleCancelPublication = useCallback(() => {
    if (isPublishing) {
      return;
    }
    setPublicationMode(null);
  }, [isPublishing]);

  const { dimensions: tagDimensions } = useSparkTagOptions(
    effectiveProjectTagPattern,
    { dimensionFqns, schemaPattern: creativeType?.creative_tag_schema_fqn || null }
  );

  // Tag constraints for TagContextSelector
  const allowedDimensionFqns = useMemo(() => {
    const fqns = new Set<string>();
    effectiveProjectTagPattern?.forEach((entry) => fqns.add(entry.dimension_fqn));
    creativeType?.creative_tag_schema_fqn?.forEach((entry) => fqns.add(entry.dimension_fqn));
    return fqns.size > 0 ? fqns : null;
  }, [effectiveProjectTagPattern, creativeType?.creative_tag_schema_fqn]);

  const allowedElementFqnsByDimension = useMemo(() => {
    if (!effectiveProjectTagPattern) return null;
    const map = new Map<string, Set<string>>();
    effectiveProjectTagPattern.forEach((entry) => {
      map.set(entry.dimension_fqn, new Set(entry.element_fqns));
    });
    return map;
  }, [effectiveProjectTagPattern]);

  const strictDimensions = useMemo(() => {
    if (!effectiveProjectTagPattern) return null;
    const set = new Set<string>();
    effectiveProjectTagPattern.forEach((entry) => {
      if (entry.is_strict) set.add(entry.dimension_fqn);
    });
    return set;
  }, [effectiveProjectTagPattern]);

  const tagConstraints = useMemo<TagContextSelectorConstraints | undefined>(() => {
    if (!effectiveProjectTagPattern && !creativeType?.creative_tag_schema_fqn?.length) return undefined;
    return {
      allowedDimensionFqns: allowedDimensionFqns ?? undefined,
      allowedElementFqnsByDimension: allowedElementFqnsByDimension ?? EMPTY_ELEMENT_MAP,
      strictDimensions: strictDimensions ?? undefined,
    };
  }, [allowedDimensionFqns, allowedElementFqnsByDimension, effectiveProjectTagPattern, creativeType?.creative_tag_schema_fqn, strictDimensions]);

  const isContextDisabled = isReadOnly;

  // ==============================
  // Item Mode: load item + render
  // ==============================
  const getLibraryItem = useAtlasStore((state: AtlasState) => state.getLibraryItem);
  const updateLibraryItem = useAtlasStore((state: AtlasState) => state.updateLibraryItem);
  const publishLibraryItem = useAtlasStore((state: AtlasState) => state.publishLibraryItem);
  const unpublishLibraryItem = useAtlasStore((state: AtlasState) => state.unpublishLibraryItem);
  const deleteLibraryItem = useAtlasStore((state: AtlasState) => state.deleteLibraryItem);
  const sparkCreativeTypes = useAtlasStore((state: AtlasState) => state.sparkCreativeTypes);
  const fetchSparkCreativeTypes = useAtlasStore((state: AtlasState) => state.fetchSparkCreativeTypes);

  const [item, setItem] = useState<any | null>(null);
  const [isLoadingItem, setIsLoadingItem] = useState(false);
  const [itemError, setItemError] = useState<string | null>(null);
  const isProjectOriginItem = useMemo(() => !!(item?.source_creative_fqn || item?.project_fqn), [item]);
  const [originPermissionChecked, setOriginPermissionChecked] = useState(false);
  const [originProjectCanPublish, setOriginProjectCanPublish] = useState(false);
  // Item title/description editing state
  const [isEditingItemTitle, setIsEditingItemTitle] = useState(false);
  const [editItemTitle, setEditItemTitle] = useState('');
  const [isSavingItemTitle, setIsSavingItemTitle] = useState(false);
  const itemTitleInputRef = useRef<HTMLInputElement>(null);
  const [isEditingItemDescription, setIsEditingItemDescription] = useState(false);
  const [editItemDescription, setEditItemDescription] = useState('');
  const [isSavingItemDescription, setIsSavingItemDescription] = useState(false);
  const itemDescriptionInputRef = useRef<HTMLInputElement>(null);

  const iterationLike = useMemo(() => (item ? adaptLibraryItemToIteration(item) as SparkIteration : null), [item]);
  const formsFromItem: Record<string, SparkFormDefinition> = useMemo(() => {
    return buildSparkFormDefinitions(item?.forms_overlay, item?.elements_definition) ?? {};
  }, [item]);
  const orderedItemFormIds = useMemo(() => Object.keys(formsFromItem || {}), [formsFromItem]);

  const itemCreativeTypeFqn: string | undefined = (item as any)?.creative_type_fqn as any;
  const creativeTypeFqnFallback: string | undefined = creative?._creative_type_fqn as any;
  const itemCreativeType = useMemo(() => {
    if (!isItemMode) return undefined;
    const allTypes = sparkCreativeTypes || [];
    const typeFqn = itemCreativeTypeFqn || creativeTypeFqnFallback;
    if (!typeFqn) return creativeType;
    const match = allTypes.find((ct: SparkCreativeType) => String(ct.fqn) === String(typeFqn));
    return match || creativeType;
  }, [isItemMode, sparkCreativeTypes, itemCreativeTypeFqn, creativeTypeFqnFallback, creativeType]);

  const effectiveOrderedFormIds = isItemMode ? orderedItemFormIds : iterationOrderedFormIds;
  const effectiveFormsMap = isItemMode ? formsFromItem : iterationFormsMap;
  const effectiveOverlaySources = isItemMode
    ? [item?.forms_overlay, itemCreativeType?.forms_overlay]
    : [creative?.forms_overlay, iterationCreativeType?.forms_overlay];

  // Build sections array for sidebar (forms + attachments)
  const navSections = useMemo(() => {
    const sections = effectiveOrderedFormIds.map((fid) => ({
      id: fid,
      title: resolveSparkFormLabelFromOverlay(fid, ...effectiveOverlaySources) ?? formatSparkFormLabel(fid, effectiveFormsMap[fid]),
    }));
    sections.push({ id: 'attachments', title: 'Attachments' });
    return sections;
  }, [effectiveFormsMap, effectiveOrderedFormIds, effectiveOverlaySources]);

  // IntersectionObserver scroll spy (mirrors UnifiedViewer pattern)
  useEffect(() => {
    const scrollContainer = contentScrollRef.current;
    if (!scrollContainer || navSections.length === 0) return;

    const frameId = requestAnimationFrame(() => {
      const els = Object.values(sectionRefs.current).filter(Boolean) as HTMLDivElement[];
      if (els.length === 0) return;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter(e => e.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible.length > 0) {
            const id = visible[0].target.getAttribute('data-section-id');
            if (id) setActiveSectionId(id);
          }
        },
        {
          root: scrollContainer,
          rootMargin: '-10% 0px -80% 0px',
          threshold: 0,
        }
      );

      els.forEach(el => observerRef.current!.observe(el));
    });

    return () => {
      cancelAnimationFrame(frameId);
      observerRef.current?.disconnect();
    };
  }, [navSections]);

  // Save/restore scroll position when switching tabs (both tabs share viewerContentScroll)
  useEffect(() => {
    const prev = prevTabRef.current;
    if (prev === controlledActiveTab) return;
    if (controlledActiveTab === 'deepsights') {
      prevTabRef.current = controlledActiveTab;
      return;
    }
    const scroller = contentScrollRef.current;

    // Save outgoing tab's scroll position
    if (scroller && prev !== 'deepsights') {
      savedScrollPositions.current[prev] = scroller.scrollTop;
    }

    // Restore incoming tab's position (0 if never visited) — deferred so DOM renders first
    requestAnimationFrame(() => {
      if (!contentScrollRef.current) return;
      // If there's a pending section scroll, handle it instead of restoring position
      const pendingSection = pendingSectionScrollRef.current;
      if (pendingSection) {
        pendingSectionScrollRef.current = null;
        const el = sectionRefs.current[pendingSection];
        if (el) {
          const scr = contentScrollRef.current;
          const elRect = el.getBoundingClientRect();
          const parentRect = scr.getBoundingClientRect();
          scr.scrollTo({
            top: scr.scrollTop + (elRect.top - parentRect.top),
            behavior: 'smooth',
          });
          return;
        }
      }
      if (prev !== 'deepsights') {
        contentScrollRef.current.scrollTop = savedScrollPositions.current[controlledActiveTab] ?? 0;
      }
    });

    prevTabRef.current = controlledActiveTab;
  }, [controlledActiveTab]);

  // Click-to-scroll handler for sidebar
  const handleSidebarSectionClick = useCallback((sectionId: string) => {
    setActiveSectionId(sectionId);
    if (controlledActiveTab !== 'details') {
      // Switching from another tab — defer scroll until form content renders
      pendingSectionScrollRef.current = sectionId;
      setControlledActiveTab('details');
    } else {
      // Already on details tab — scroll immediately
      const el = sectionRefs.current[sectionId];
      const scroller = contentScrollRef.current;
      if (el && scroller) {
        const elRect = el.getBoundingClientRect();
        const parentRect = scroller.getBoundingClientRect();
        scroller.scrollTo({
          top: scroller.scrollTop + (elRect.top - parentRect.top),
          behavior: 'smooth',
        });
      }
    }
  }, [controlledActiveTab]);

  const { user } = useAuth();
  const isTenantAdmin = !!(user?.roles || []).find((r: string) => r.toLowerCase().includes('admin'));
  const isOwner = !!(item && item.owner_fqn && user?.id === item.owner_fqn);
  const itemIsPublished = item?.state === 'published';

  // Load permission for project-origin items (use project admin flag as publish right)
  const itemSourceProjectFqn: string | undefined = (item as any)?.source_project_fqn as any;
  const itemProjectFqn: string | undefined = (item as any)?.project_fqn as any;
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setOriginPermissionChecked(false);
      setOriginProjectCanPublish(false);
      try {
        if (isProjectOriginItem) {
          const projFqn = itemSourceProjectFqn || itemProjectFqn;
          if (projFqn) {
            const proj = await atlasClient.getSparkProject(projFqn);
            if (!cancelled) {
              setOriginProjectCanPublish(!!proj.current_user_is_admin);
              setOriginPermissionChecked(true);
            }
          } else {
            if (!cancelled) setOriginPermissionChecked(true);
          }
        } else {
          if (!cancelled) setOriginPermissionChecked(true);
        }
      } catch (e) {
        if (!cancelled) setOriginPermissionChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [isProjectOriginItem, itemSourceProjectFqn, itemProjectFqn]);

  useEffect(() => {
    if (!isItemMode || !isOpen) return;
    (async () => {
      if (!itemFqn) { setItemError('Missing item FQN'); return; }
      setItemError(null);
      setIsLoadingItem(true);
      try {
        if (!sparkCreativeTypes || sparkCreativeTypes.length === 0) {
          try { await fetchSparkCreativeTypes(); } catch (_) { }
        }
        const data = await getLibraryItem(itemFqn);
        setItem(data);
      } catch (e: any) {
        setItemError(e?.message || 'Failed to load item');
      } finally {
        setIsLoadingItem(false);
      }
    })();
  }, [isItemMode, isOpen, itemFqn, getLibraryItem, sparkCreativeTypes, fetchSparkCreativeTypes]);

  

  const persistItemValues = useCallback(async (values: Record<string, any>) => {
    if (!item) return iterationLike!;
    const updated = await updateLibraryItem(item.fqn, { values });
    setItem(updated);
    return adaptLibraryItemToIteration(updated) as SparkIteration;
  }, [item, updateLibraryItem, iterationLike]);

  const itemTypeAllowsPublish = !!itemCreativeType?.library;

  // Capabilities per requirements
  const itemCanPublish = useMemo(() => {
    if (!itemTypeAllowsPublish || isProjectOriginItem) return false;
    return !itemIsPublished && isOwner;
  }, [itemTypeAllowsPublish, isProjectOriginItem, itemIsPublished, isOwner]);

  // Strict permission to render unpublish
  const itemCanUnpublish = useMemo(() => {
    if (!itemTypeAllowsPublish) return false;
    if (isProjectOriginItem) return originPermissionChecked && originProjectCanPublish;
    return itemIsPublished && (isOwner || isTenantAdmin);
  }, [
    itemTypeAllowsPublish,
    isProjectOriginItem,
    originPermissionChecked,
    originProjectCanPublish,
    itemIsPublished,
    isOwner,
    isTenantAdmin,
  ]);

  const itemCanDelete = useMemo(() => {
    if (isProjectOriginItem) return false;
    return !itemIsPublished && isOwner;
  }, [isProjectOriginItem, itemIsPublished, isOwner]);

  const itemCanEditValues = !itemIsPublished && !isProjectOriginItem && isOwner;
  const [isItemPublishing, setIsItemPublishing] = useState(false);
  const [itemPublicationMode, setItemPublicationMode] = useState<'publish' | 'unpublish' | null>(null);
  const [isItemDeleteModalOpen, setIsItemDeleteModalOpen] = useState(false);
  const [isItemDeleting, setIsItemDeleting] = useState(false);

  // Item mode: tag editing constrained by creative type schema
  const itemSchemaPattern = useMemo(() => (itemCreativeType?.creative_tag_schema_fqn || null), [itemCreativeType?.creative_tag_schema_fqn]);
  const itemSchemaDimensionFqns = useMemo(() => {
    return itemSchemaPattern ? new Set(itemSchemaPattern.map(e => e.dimension_fqn)) : undefined;
  }, [itemSchemaPattern]);
  const { dimensions: itemTagDimensions } = useSparkTagOptions(
    isItemMode && item ? (item.tag_pattern || []) : [],
    {
      dimensionFqns: isItemMode ? (itemSchemaDimensionFqns || new Set<string>()) : itemSchemaDimensionFqns,
      schemaPattern: itemSchemaPattern,
    }
  );
  const [isSavingItemTags, setIsSavingItemTags] = useState(false);
  const [itemTagError, setItemTagError] = useState<string | null>(null);

  // Item mode tag constraints (for TagContextSelector in header)
  const itemAllowedDimensionFqns = useMemo(() => {
    if (!itemSchemaPattern) return null;
    return new Set(itemSchemaPattern.map((e: any) => e.dimension_fqn));
  }, [itemSchemaPattern]);

  const itemAllowedElementFqnsByDimension = useMemo(() => {
    if (!itemSchemaPattern) return null;
    const map = new Map<string, Set<string>>();
    itemSchemaPattern.forEach((entry: any) => {
      const elems = entry.element_fqns || [];
      if ((elems || []).length > 0) {
        map.set(entry.dimension_fqn, new Set(elems));
      }
    });
    return map;
  }, [itemSchemaPattern]);

  const itemStrictDimensions = useMemo(() => {
    if (!itemSchemaPattern) return null;
    const set = new Set<string>();
    itemSchemaPattern.forEach((entry: any) => { if (entry.is_strict) set.add(entry.dimension_fqn); });
    return set;
  }, [itemSchemaPattern]);

  const itemTagConstraints = useMemo<TagContextSelectorConstraints | undefined>(() => {
    if (!itemSchemaPattern) return undefined;
    return {
      allowedDimensionFqns: itemAllowedDimensionFqns ?? undefined,
      allowedElementFqnsByDimension: itemAllowedElementFqnsByDimension ?? EMPTY_ELEMENT_MAP,
      strictDimensions: itemStrictDimensions ?? undefined,
    };
  }, [itemAllowedDimensionFqns, itemAllowedElementFqnsByDimension, itemStrictDimensions, itemSchemaPattern]);

  const isItemContextDisabled = Boolean(!itemCanEditValues || isLoadingItem || isSavingItemTags);
  const handlePersistItemTags = useCallback(async (nextPattern: any[]) => {
    if (!item) return;
    if (!itemCanEditValues) return; // read-only in published
    setIsSavingItemTags(true);
    setItemTagError(null);
    try {
      const updated = await updateLibraryItem(item.fqn, { tag_pattern: nextPattern });
      setItem(updated);
    } catch (e: any) {
      const message = e?.response?.data?.error || (e instanceof Error ? e.message : 'Failed to update tags');
      setItemTagError(message);
      toast.error(message);
      throw e;
    } finally {
      setIsSavingItemTags(false);
    }
  }, [item, itemCanEditValues, updateLibraryItem]);

  // Lightweight polling for library item mode to reflect changes (e.g., AI image jobs)
  useEffect(() => {
    if (!isItemMode || !isOpen || !itemFqn) return;
    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        // Skip while any local save is in-flight to avoid jank
        // Also skip when UI has an interaction-blocking modal (e.g., SparkActionModal)
        if (
          isSavingItemTitle ||
          isSavingItemDescription ||
          isSavingItemTags ||
          isItemPublishing ||
          isItemDeleting ||
          useAtlasStore.getState().isUserInteractionBlockingRefresh
        ) {
          return;
        }
        const latest = await getLibraryItem(itemFqn);
        // Update only if timestamp changed or item missing locally
        const prevUpdated = (item as any)?._updated_at || null;
        const nextUpdated = (latest as any)?._updated_at || null;
        if (!cancelled && nextUpdated && nextUpdated !== prevUpdated) {
          setItem(latest);
        }
      } catch (_) {
        // swallow errors during polling
      }
    }, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isItemMode, isOpen, itemFqn, getLibraryItem, isSavingItemTitle, isSavingItemDescription, isSavingItemTags, isItemPublishing, isItemDeleting, item]);

  // Compute hero image info from item images (prefer creative-type hero slot)
  const itemHeroInfo = useMemo(() => {
    if (!isItemMode || !item) return null;
    const images = (item as any).images || {};
    let fileId: string | null = null;
    let elementId: string | null = null;
    const heroId = (itemCreativeType as any)?.hero_image_element_id;
    if (heroId && images[heroId] && Array.isArray(images[heroId].current_file_ids) && images[heroId].current_file_ids.length > 0) {
      fileId = images[heroId].current_file_ids[0];
      elementId = heroId;
    } else {
      for (const key of Object.keys(images)) {
        const slot = images[key];
        const current = slot?.current_file_ids;
        if (Array.isArray(current) && current.length > 0) {
          fileId = current[0];
          elementId = key;
          break;
        }
      }
    }
    if (!fileId || !elementId) return null;
    return { url: atlasClient.getAssetUrl(fileId), fileId, elementId };
  }, [isItemMode, item, itemCreativeType]);

  // Sync item title/description editors
  useEffect(() => {
    setEditItemTitle(item?.title || '');
  }, [item?.title]);
  useEffect(() => {
    if (isEditingItemTitle) {
      setTimeout(() => {
        itemTitleInputRef.current?.focus();
        itemTitleInputRef.current?.select();
      }, 0);
    }
  }, [isEditingItemTitle]);
  useEffect(() => {
    setEditItemDescription(item?.description || '');
  }, [item?.description]);
  useEffect(() => {
    if (isEditingItemDescription) {
      setTimeout(() => {
        itemDescriptionInputRef.current?.focus();
        itemDescriptionInputRef.current?.select();
      }, 0);
    }
  }, [isEditingItemDescription]);

  const handleStartEditingItemTitle = useCallback(() => {
    if (!itemCanEditValues) { toast.info('Item is published'); return; }
    setEditItemTitle(item?.title || '');
    setIsEditingItemTitle(true);
  }, [item?.title, itemCanEditValues]);
  const handleCancelEditingItemTitle = useCallback(() => {
    setIsEditingItemTitle(false);
    setEditItemTitle(item?.title || '');
  }, [item?.title]);
  const handleSaveItemTitle = useCallback(async () => {
    if (!item) return;
    const next = (editItemTitle || '').trim();
    const current = item.title || '';
    if (!next) {
      toast.error('Title cannot be empty');
      return;
    }
    if (next === current) { setIsEditingItemTitle(false); return; }
    setIsSavingItemTitle(true);
    try {
      const updated = await updateLibraryItem(item.fqn, { title: next });
      setItem(updated);
      setIsEditingItemTitle(false);
      toast.success('Title updated');
      onDataRefreshed?.();
    } catch (e: any) {
      const message = e?.response?.data?.error || (e instanceof Error ? e.message : 'Failed to update title');
      toast.error(message);
    } finally {
      setIsSavingItemTitle(false);
    }
  }, [item, editItemTitle, updateLibraryItem, onDataRefreshed]);

  const handleStartEditingItemDescription = useCallback(() => {
    if (!itemCanEditValues) { toast.info('Item is published'); return; }
    setEditItemDescription(item?.description || '');
    setIsEditingItemDescription(true);
  }, [item?.description, itemCanEditValues]);
  const handleCancelEditingItemDescription = useCallback(() => {
    setIsEditingItemDescription(false);
    setEditItemDescription(item?.description || '');
  }, [item?.description]);
  const handleSaveItemDescription = useCallback(async () => {
    if (!item) return;
    const next = (editItemDescription || '').trim();
    const current = item.description || '';
    if (!next) {
      toast.error('Description cannot be empty');
      return;
    }
    if (next === current) { setIsEditingItemDescription(false); return; }
    setIsSavingItemDescription(true);
    try {
      const updated = await updateLibraryItem(item.fqn, { description: next });
      setItem(updated);
      setIsEditingItemDescription(false);
      toast.success('Description updated');
      onDataRefreshed?.();
    } catch (e: any) {
      const message = e?.response?.data?.error || (e instanceof Error ? e.message : 'Failed to update description');
      toast.error(message);
    } finally {
      setIsSavingItemDescription(false);
    }
  }, [item, editItemDescription, updateLibraryItem, onDataRefreshed]);

  const handlePublishItem = useCallback(async () => {
    if (!item || !itemCanPublish) return;
    setIsItemPublishing(true);
    try {
      const updated = await publishLibraryItem(item.fqn);
      setItem(updated);
      toast.success('Item published');
      onDataRefreshed?.();
    } catch (e: any) {
      const message = e?.response?.data?.error || (e instanceof Error ? e.message : 'Failed to publish item');
      toast.error(message);
    } finally {
      setIsItemPublishing(false);
    }
  }, [item, itemCanPublish, publishLibraryItem, onDataRefreshed]);

  const handleUnpublishItem = useCallback(async () => {
    if (!item || !itemCanUnpublish) return;
    setIsItemPublishing(true);
    try {
      // Server handles both personal (to draft) and project-origin (unlink creative and remove item) cases
      const resp = await unpublishLibraryItem(item.fqn);
      // For personal items, API returns updated item (now draft); for project-origin, returns { success: true }
      if (!isProjectOriginItem && resp) setItem(resp as any);
      onDataRefreshed?.();
      toast.success(isProjectOriginItem ? 'Unpublished and removed from Library' : 'Item moved to Draft');
      if (isProjectOriginItem) {
        onClose();
      }
    } catch (e: any) {
      const message = e?.response?.data?.error || (e instanceof Error ? e.message : 'Failed to unpublish item');
      toast.error(message);
    } finally {
      setIsItemPublishing(false);
    }
  }, [item, itemCanUnpublish, unpublishLibraryItem, isProjectOriginItem, onClose, onDataRefreshed]);

  const handleDeleteItem = useCallback(async () => {
    if (!item || !itemCanDelete) return;
    setIsItemDeleting(true);
    try {
      await deleteLibraryItem(item.fqn);
      toast.success('Item deleted');
      onDataRefreshed?.();
      setIsItemDeleteModalOpen(false);
      onClose();
    } catch (e: any) {
      const message = e?.response?.data?.error || (e instanceof Error ? e.message : 'Failed to delete item');
      toast.error(message);
    } finally {
      setIsItemDeleting(false);
    }
  }, [item, itemCanDelete, deleteLibraryItem, onClose, onDataRefreshed]);

  // Function to fetch/refresh creative and iteration data based on the displayedCreativeFqn
  const refreshCreativeData = useCallback(async (preserveDisplayedIteration = false) => {
    if (!displayedCreativeFqn) return;

    setIsLoadingCreative(true);
    setIsLoadingIterations(true);
    setSparkUserInteractionBlockingRefresh(true);
    setError(null);

    try {
      const creativeData = await fetchJSON<SparkCreative>(
        `/api/spark/creatives/${encodeURIComponent(displayedCreativeFqn)}`
      );
      setCreative(creativeData);
      useAtlasStore.setState(state => ({
        sparkFormDefinitions: {
          ...state.sparkFormDefinitions,
          [creativeData.fqn]: (() => {
            return buildSparkFormDefinitions(creativeData.forms_overlay, creativeData.elements_definition);
          })(),
        },
      }));

      const iterationsData = await atlasClient.getSparkIterations(displayedCreativeFqn);
      setIterations(iterationsData);
      cacheSparkIterations(iterationsData);

      const activeIterFqn = creativeData._active_iteration_fqn ||
        (iterationsData.length > 0 ? iterationsData[0].fqn : null);
      setActiveIterationFqn(activeIterFqn);

      // Determine which iteration to display after refresh
      if (preserveDisplayedIteration && displayedIterationFqn) {
        // Check if the previously displayed iteration still exists
        const displayedExists = iterationsData.some(iter => iter.fqn === displayedIterationFqn);
        if (!displayedExists) {
          // Fallback to active if the displayed one was deleted
          setDisplayedIterationFqn(activeIterFqn);
        }
        // Otherwise, keep the current displayedIterationFqn
      } else {
        const requestedIteration =
          initialIterationFqn && iterationsData.some(iter => iter.fqn === initialIterationFqn)
            ? initialIterationFqn
            : null;
        // Pick the newest unseen iteration if there is one, otherwise use the active iteration.
        const defaultIter = getNewestUnseenIteration(iterationsData);
        setDisplayedIterationFqn(requestedIteration ?? defaultIter?.fqn ?? activeIterFqn);
      }

      // (Parent refresh handled separately)

    } catch (error) {
      console.error('Failed to refresh creative data:', error);
      setError('Failed to load creative data. Please try again.');
    } finally {
      setIsLoadingCreative(false);
      setIsLoadingIterations(false);
      setSparkUserInteractionBlockingRefresh(false);
    }
  }, [cacheSparkIterations, displayedCreativeFqn, displayedIterationFqn, initialIterationFqn, setSparkUserInteractionBlockingRefresh]);

  // Function to fetch the list of unseen creatives for the current type
  const fetchUnseenCreatives = useCallback(async () => {
    if (!projectFqn || !creativeType?.fqn) {
      setUnseenCreativeFqns([]);
      return;
    }
    try {
      const unseenFqns = await fetchJSON<string[]>(
        `/api/spark/creatives/project/${encodeURIComponent(projectFqn)}/creative_type/${encodeURIComponent(creativeType.fqn)}/unseen`
      );
      setUnseenCreativeFqns(unseenFqns);
    } catch (error) {
      console.error('Failed to fetch unseen creatives:', error);
      setUnseenCreativeFqns([]); // Clear on error
      // Optionally show a toast or error message to the user
    }
  }, [projectFqn, creativeType?.fqn]);

  const handleConfirmPublication = useCallback(async () => {
    if (!publicationMode) {
      return;
    }
    if (!creative) {
      toast.error('Creative unavailable. Please reopen.');
      setPublicationMode(null);
      return;
    }
    if (!projectFqn) {
      toast.error('Project context unavailable.');
      setPublicationMode(null);
      return;
    }

    setIsPublishing(true);
    try {
      if (publicationMode === 'publish') {
        const iterationFqn = displayedIterationFqn || creative._active_iteration_fqn;
        if (!iterationFqn) {
          throw new Error('No active iteration to publish from');
        }
        await createLibraryItemFromIteration(iterationFqn);
        toast.success('Published to Library');
        // Keep editor open; refresh project/creative state
        await refreshCreativeData(true);
        onDataRefreshed?.();
      } else if (publicationMode === 'unpublish') {
        // Use backend creative-unpublish endpoint to clear publish flags and remove snapshot atomically
        await authenticatedFetch(`/api/spark/library/creatives/${encodeURIComponent(creative.fqn)}/unpublish`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }).then(parseJsonResponse);
        toast.success('Unpublished and creative unlocked');
        // Optimistically update local state; then refresh
        setCreative(prev => prev ? ({ ...prev, is_published: false, published_at: null, published_by: null, published_snapshot_fqn: null }) as SparkCreative : prev);
        await refreshCreativeData(true);
        onDataRefreshed?.();
      }

      await refreshCreativeData(true);
      await fetchUnseenCreatives();
      onDataRefreshed?.();
    } catch (error: any) {
      const message = error?.response?.data?.error || (error instanceof Error ? error.message : 'Publication update failed');
      toast.error(message);
    } finally {
      setIsPublishing(false);
      setPublicationMode(null);
    }
  }, [publicationMode, creative, projectFqn, refreshCreativeData, fetchUnseenCreatives, onDataRefreshed, displayedIterationFqn, createLibraryItemFromIteration]);

  const handlePersistCreativeTags = useCallback(async (nextPattern: SparkTagDimensionSelection[]) => {
    if (!creative) {
      return;
    }

    if (isReadOnly) {
      toast.info(isPublished ? 'Creative is published' : 'Project is closed');
      return;
    }

    // Note: Don't use setIsSavingTags here - TagContextSelector manages its own
    // persist state internally via selfPersistingRef. Using setIsSavingTags causes
    // multiple re-renders that produce visible flicker.
    setTagError(null);

    // Store the persist promise so handleClose can wait for it
    const persistPromise = (async () => {
      try {
        await updateSparkCreative(creative.fqn, { tag_pattern: nextPattern });
        // Optimistically update local state to avoid flicker from full refresh
        setCreative(prev => prev ? { ...prev, tag_pattern: nextPattern } : prev);
        onDataRefreshed?.();
      } catch (error: any) {
        const message = error?.response?.data?.error || (error instanceof Error ? error.message : 'Failed to update context');
        setTagError(message);
        toast.error(`Failed to update tags: ${message}`);
        throw error;
      } finally {
        pendingTagPersistRef.current = null;
      }
    })();

    pendingTagPersistRef.current = persistPromise;
    return persistPromise;
  }, [creative, updateSparkCreative, onDataRefreshed, isReadOnly, isPublished]);

  // Handle ESC key to close the modal
  const handleClose = useCallback(async () => {
    if (onClose) {
      // Wait for any pending tag persist to complete before closing
      // This ensures the server has the updated data before we re-fetch on reopen
      if (pendingTagPersistRef.current) {
        try {
          await pendingTagPersistRef.current;
        } catch {
          // Persist failed, but we still want to close
        }
      }
      // Notify parent to refresh board data before closing
      onDataRefreshed?.();
      onClose();
    }
  }, [onClose, onDataRefreshed]);

  // Add ESC key event listener to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      // Don't close the modal if the event target is a form element
      const isFormElementFocused =
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement;

      if (event.key === 'Escape' && isOpen && !isFormElementFocused) {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, handleClose]); // Re-create listener if these dependencies change

  // Apply open class one frame after mounting to enable CSS transition without flicker
  useEffect(() => {
    if (isOpen) {
      openRafRef.current = requestAnimationFrame(() => setAnimateOpen(true));
      return () => {
        if (openRafRef.current) cancelAnimationFrame(openRafRef.current);
        setAnimateOpen(false);
      };
    } else {
      setAnimateOpen(false);
    }
  }, [isOpen]);

  // Fetch initial data/reset when modal opens or creative FQN prop changes
  useEffect(() => {
    if (isOpen) {
      // Reset displayed creative FQN when the prop changes
      setDisplayedCreativeFqn(creativeFqn);
      setDisplayedIterationFqn(initialIterationFqn || null);
      setControlledActiveTab(initialTab || 'details');
      setScrollToFeedbackId(initialFeedbackId || null);
      // The effect below will now handle fetching based on this change
    }
  }, [isOpen, creativeFqn, initialFeedbackId, initialIterationFqn, initialTab]); // Only depend on open state and incoming FQN

  // --> New Effect: Fetch data whenever displayedCreativeFqn changes <--
  useEffect(() => {
    if (isOpen && displayedCreativeFqn) {
      // Preserve the currently selected iteration when refreshing
      refreshCreativeData(true);
      fetchUnseenCreatives();
    }
  }, [isOpen, displayedCreativeFqn, refreshCreativeData, fetchUnseenCreatives]);

  // Fetch unseen list if project/type changes while open (less common case)
  useEffect(() => {
    if (isOpen) {
      fetchUnseenCreatives();
    }
  }, [isOpen, projectFqn, creativeType?.fqn, fetchUnseenCreatives]);

  // Block background refresh while modal is open
  useEffect(() => {
    if (isOpen) {
      setSparkUserInteractionBlockingRefresh(true);
      return () => {
        setSparkUserInteractionBlockingRefresh(false);
      };
    }
  }, [isOpen, setSparkUserInteractionBlockingRefresh]);

  // This function only displays the iteration without changing the active state
  const handleDisplayIteration = (iterationFqn: string) => {
    // Just update UI to show the selected iteration (no backend update)
    setDisplayedIterationFqn(iterationFqn);
    setLocallySeenAt(Date.now());
    setIsVersionHistoryOpen(false);
  };

  // This function sets the iteration as the active one in the backend
  const handleSetActiveIteration = async (iterationFqn: string) => {
    if (isReadOnly) {
      toast.info(isPublished ? 'Creative is published' : 'Project is closed');
      return;
    }
    try {
      await authenticatedFetch(`/api/spark/creatives/active_iteration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // Use the currently displayed creative if available (prevents targeting a stale creative when navigating)
          creative_fqn: displayedCreativeFqn || creativeFqn,
          iteration_fqn: iterationFqn,
        }),
      }).then(parseJsonResponse);

      // Refresh data to ensure consistency, keep current display if possible
      await refreshCreativeData(true);
      // Notify parent to refresh board data and reload hero image
      onDataRefreshed?.();

    } catch (error) {
      console.error('Failed to update active iteration:', error);
      const message = error instanceof Error ? error.message : "An unknown error occurred.";
      toast.error(`Failed to update active iteration: ${message}`);
    }
  };

  // Check if there's a retry needed
  const handleRetry = () => {
    refreshCreativeData(); // Just call the refresh function
  };

  // Handle delete iteration request
  const handleDeleteIterationRequest = (iterationFqn: string) => {
    setIterationToDeleteFqn(iterationFqn);
    setIsConfirmDeleteOpen(true);
  };

  // Handle confirm delete iteration
  const handleConfirmDeleteIteration = async () => {
    if (!iterationToDeleteFqn) return;
    if (isReadOnly) {
      toast.info(isPublished ? 'Creative is published' : 'Project is closed');
      setIsConfirmDeleteOpen(false);
      setIterationToDeleteFqn(null);
      return;
    }
    setIsDeletingIteration(true);
    try {
      await authenticatedFetch(`/api/spark/iterations/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ iteration_fqn: iterationToDeleteFqn }),
      }).then(parseJsonResponse);
      // Refresh data and reset displayedIterationFqn if needed
      await refreshCreativeData();
      toast.success('Iteration deleted successfully');
    } catch (error) {
      console.error('Failed to delete iteration:', error);
      toast.error('Failed to delete iteration. Please try again.');
    } finally {
      setIsDeletingIteration(false);
      setIsConfirmDeleteOpen(false);
      setIterationToDeleteFqn(null);
    }
  };

  // Clear scroll-to-feedback timestamp after scroll completes
  const handleScrollComplete = useCallback(() => {
    setScrollToFeedbackTimestamp(null);
    setScrollToFeedbackId(null);
  }, []);

  const handleOpenScopedActivityPanel = useCallback(() => {
    if (!creative) {
      return;
    }

    setActivityPanelScope({
      creativeFqn: creative.fqn,
      iterationFqns: iterations.map((iteration) => iteration.fqn),
    });
    setIsActivityPanelOpen(true);
  }, [creative, iterations]);

  const handleOpenUnfilteredActivityPanel = useCallback(() => {
    setActivityPanelScope(null);
    setIsActivityPanelOpen(true);
  }, []);

  const handleDismissJobCompletion = useCallback(async (jobFqn: string) => {
    await markSparkJobAsSeen([jobFqn]);
  }, [markSparkJobAsSeen]);

  const handleNavigateToJobResult = useCallback(async (job: SparkJob) => {
    const navigation = getJobResultNavigation(job, {
      navigateToStep,
      openCreativeEditor,
      resolveIteration,
      resolveFeedbackIdForJob,
      scrollToFeedback,
    });

    if (!navigation) {
      return;
    }

    await navigation.action();
  }, [
    navigateToStep,
    openCreativeEditor,
    resolveFeedbackIdForJob,
    resolveIteration,
    scrollToFeedback,
  ]);

  // Add handler for creating a new iteration
  const handleCreateIteration = async (iterationFqn: string) => {
    if (isReadOnly) {
      toast.info(isPublished ? 'Creative is published' : 'Project is closed');
      return;
    }
    try {
      await authenticatedFetch(`/api/spark/iterations/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ iteration_fqn: iterationFqn }),
      }).then(parseJsonResponse);
      // Refresh data after creation
      await refreshCreativeData();
      toast.success('New iteration created successfully');
    } catch (error) {
      console.error('Failed to create iteration:', error);
      const message = error instanceof Error ? error.message : 'An unknown error occurred.';
      toast.error(`Failed to create iteration: ${message}`);
    }
  };

  // Detect project job completions to update iteration list and active status
  useEffect(() => {
    if (editorMode === 'item' || !creativeFqn) {
      prevCompletedCountRef.current = completedJobCount ?? null;
      return;
    }

    if (
      prevCompletedCountRef.current != null &&
      completedJobCount != null &&
      completedJobCount > prevCompletedCountRef.current
    ) {
      void refreshCreativeData(true).catch((error) => {
        console.error('Failed to refresh editor data on job completion:', error);
      });
      onDataRefreshed?.();
    }

    prevCompletedCountRef.current = completedJobCount ?? null;
  }, [completedJobCount, creativeFqn, editorMode, onDataRefreshed, refreshCreativeData]);

  // Calculate if there's a *next* unseen creative (different from the current one)
  const hasNextUnseen = useMemo(() => {
    return unseenCreativeFqns.filter(fqn => fqn !== displayedCreativeFqn).length > 0;
  }, [unseenCreativeFqns, displayedCreativeFqn]);

  // Function to navigate to the next unseen creative
  const handleNextUnseen = useCallback(async () => {
    if (!hasNextUnseen) return;

    const filteredUnseen = unseenCreativeFqns.filter(fqn => fqn !== displayedCreativeFqn);
    if (filteredUnseen.length === 0) return; // Should not happen if hasNextUnseen is true, but safety check

    const currentIndex = unseenCreativeFqns.indexOf(displayedCreativeFqn);
    let nextIndex = -1;

    // Find the next unseen FQN in the original list, wrapping around
    if (currentIndex === -1) {
      // Current creative wasn't in the unseen list (it became seen), just pick the first from filtered
      nextIndex = unseenCreativeFqns.indexOf(filteredUnseen[0]);
    } else {
      let searchIndex = (currentIndex + 1) % unseenCreativeFqns.length;
      while (searchIndex !== currentIndex) {
        if (unseenCreativeFqns[searchIndex] !== displayedCreativeFqn) {
          // Found the next unseen one
          nextIndex = searchIndex;
          break;
        }
        searchIndex = (searchIndex + 1) % unseenCreativeFqns.length;
      }
      // If the loop completes without finding another unseen, pick the first filtered (handles edge case of only one unseen which is the current one, although hasNextUnseen should prevent this)
      if (nextIndex === -1) {
        nextIndex = unseenCreativeFqns.indexOf(filteredUnseen[0]);
      }
    }


    if (nextIndex !== -1) {
      const nextFqn = unseenCreativeFqns[nextIndex];
      setDisplayedCreativeFqn(nextFqn); // Update the displayed creative
    }

  }, [hasNextUnseen, unseenCreativeFqns, displayedCreativeFqn]);

  // Keep creative name state in sync and focus input when toggling edit
  useEffect(() => {
    setEditCreativeName(creative?.name ?? '');
  }, [creative?.name]);

  useEffect(() => {
    if (isEditingCreativeName) {
      // Focus and select text on edit start
      setTimeout(() => {
        creativeNameInputRef.current?.focus();
        creativeNameInputRef.current?.select();
      }, 0);
    }
  }, [isEditingCreativeName]);

  // Sync description editor text with creative
  useEffect(() => {
    setEditCreativeDescription(creative?.description ?? '');
  }, [creative?.description]);

  useEffect(() => {
    if (isEditingCreativeDescription) {
      setTimeout(() => {
        creativeDescriptionInputRef.current?.focus();
        creativeDescriptionInputRef.current?.select();
      }, 0);
    }
  }, [isEditingCreativeDescription]);

  const handleStartEditingCreativeName = useCallback(() => {
    if (isReadOnly) { toast.info(isPublished ? 'Creative is published' : 'Project is closed'); return; }
    setEditCreativeName(creative?.name ?? '');
    setIsEditingCreativeName(true);
    setSparkUserInteractionBlockingRefresh(true);
  }, [creative?.name, isReadOnly, isPublished, setSparkUserInteractionBlockingRefresh]);

  const handleCancelEditingCreativeName = useCallback(() => {
    setIsEditingCreativeName(false);
    setEditCreativeName(creative?.name ?? '');
    setSparkUserInteractionBlockingRefresh(false);
  }, [creative?.name, setSparkUserInteractionBlockingRefresh]);

  const handleSaveCreativeName = useCallback(async () => {
    if (!creative) return;
    const next = editCreativeName.trim();
    const current = creative.name ?? '';
    if (!next || next === current) {
      setIsEditingCreativeName(false);
      setSparkUserInteractionBlockingRefresh(false);
      return;
    }

    setIsSavingCreativeName(true);
    try {
      await updateSparkCreative(creative.fqn, { name: next });
      await refreshCreativeData(true);
      onDataRefreshed?.();
      setIsEditingCreativeName(false);
      toast.success('Title updated');
    } catch (error: any) {
      const message = error?.response?.data?.error || (error instanceof Error ? error.message : 'Failed to update title');
      toast.error(message);
    } finally {
      setIsSavingCreativeName(false);
      setSparkUserInteractionBlockingRefresh(false);
    }
  }, [creative, editCreativeName, updateSparkCreative, refreshCreativeData, onDataRefreshed, setSparkUserInteractionBlockingRefresh]);

  const handleStartEditingCreativeDescription = useCallback(() => {
    if (isReadOnly) { toast.info(isPublished ? 'Creative is published' : 'Project is closed'); return; }
    setEditCreativeDescription(creative?.description ?? '');
    setIsEditingCreativeDescription(true);
    setSparkUserInteractionBlockingRefresh(true);
  }, [creative?.description, isReadOnly, isPublished, setSparkUserInteractionBlockingRefresh]);

  const handleCancelEditingCreativeDescription = useCallback(() => {
    setIsEditingCreativeDescription(false);
    setEditCreativeDescription(creative?.description ?? '');
    setSparkUserInteractionBlockingRefresh(false);
  }, [creative?.description, setSparkUserInteractionBlockingRefresh]);

  const handleSaveCreativeDescription = useCallback(async () => {
    if (!creative) return;
    const next = editCreativeDescription.trim();
    const current = creative.description ?? '';
    if (next === current) {
      setIsEditingCreativeDescription(false);
      setSparkUserInteractionBlockingRefresh(false);
      return;
    }

    setIsSavingCreativeDescription(true);
    try {
      await updateSparkCreative(creative.fqn, { description: next });
      await refreshCreativeData(true);
      onDataRefreshed?.();
      setIsEditingCreativeDescription(false);
      toast.success('Description updated');
    } catch (error: any) {
      const message = error?.response?.data?.error || (error instanceof Error ? error.message : 'Failed to update description');
      toast.error(message);
    } finally {
      setIsSavingCreativeDescription(false);
      setSparkUserInteractionBlockingRefresh(false);
    }
  }, [creative, editCreativeDescription, updateSparkCreative, refreshCreativeData, onDataRefreshed, setSparkUserInteractionBlockingRefresh]);

  // Item mode type label (must be before early returns to satisfy Rules of Hooks)
  const itemTypePillLabel = useMemo(() => {
    if (!isItemMode || !item) return undefined;
    const ct = (sparkCreativeTypes || []).find((t: SparkCreativeType) => t.fqn === item.creative_type_fqn);
    return ct?.name || (item.creative_type_fqn?.split('@').pop() || '').split('_').pop() || undefined;
  }, [isItemMode, item, sparkCreativeTypes]);

  // Label management handlers for HeroSection (must be before early returns)
  const handleRemoveLabelFromCreative = useCallback(async (label: string) => {
    if (isReadOnly) { toast.info(isPublished ? 'Creative is published' : 'Project is closed'); return; }
    try {
      const removeSparkLabel = useAtlasStore.getState().removeSparkLabel;
      await removeSparkLabel([creative!.fqn], label);
      await refreshCreativeData(true);
    } catch (err) {
      console.error(`Failed to remove label: ${label}`, err);
      toast.error(`Failed to remove label. Please try again.`);
    }
  }, [creative, isReadOnly, isPublished, refreshCreativeData]);

  const handleAddLabelToCreative = useCallback(async (label: string) => {
    if (isReadOnly) { toast.info(isPublished ? 'Creative is published' : 'Project is closed'); return; }
    try {
      const addSparkLabel = useAtlasStore.getState().addSparkLabel;
      await addSparkLabel([creative!.fqn], label);
      await refreshCreativeData(true);
    } catch (err) {
      console.error(`Failed to add label: ${label}`, err);
      toast.error(`Failed to add label. Please try again.`);
    }
  }, [creative, isReadOnly, isPublished, refreshCreativeData]);

  // Project labels for LabelSection (must be before early returns)
  const [availableProjectLabels, setAvailableProjectLabels] = useState<string[]>([]);
  useEffect(() => {
    if (isItemMode || !projectFqn || !isOpen) return;
    (async () => {
      try {
        const labels = await atlasClient.getSparkProjectLabels(projectFqn);
        setAvailableProjectLabels(labels.sort());
      } catch {
        setAvailableProjectLabels([]);
      }
    })();
  }, [projectFqn, isItemMode, isOpen]);

  // Displayed iteration unseen check for hero section version badge (must be before early returns)
  const displayedIterationForUnseen = displayedIterationFqn ?
    iterations.find(iteration => iteration.fqn === displayedIterationFqn) : null;
  // Hero image info for iteration mode (same logic as item mode but from iteration)
  const iterationHeroInfo = useMemo(() => {
    if (isItemMode || !displayedIterationForUnseen) return null;
    const images = (displayedIterationForUnseen as any).images || {};
    let fileId: string | null = null;
    let elementId: string | null = null;
    const heroId = (creativeType as any)?.hero_image_element_id;
    if (heroId && images[heroId] && Array.isArray(images[heroId].current_file_ids) && images[heroId].current_file_ids.length > 0) {
      fileId = images[heroId].current_file_ids[0];
      elementId = heroId;
    } else {
      for (const key of Object.keys(images)) {
        const slot = images[key];
        const current = slot?.current_file_ids;
        if (Array.isArray(current) && current.length > 0) {
          fileId = current[0];
          elementId = key;
          break;
        }
      }
    }
    if (!fileId || !elementId) return null;
    return { url: atlasClient.getAssetUrl(fileId), fileId, elementId };
  }, [isItemMode, displayedIterationForUnseen, creativeType]);

  // If not open, don't render (placed after ALL hooks to satisfy React Hooks rules)
  if (!isOpen) {
    return null;
  }

  // Early render for Item Mode (unified shell)
  if (isItemMode) {
    return (
      <div className={`${styles.modalOverlay} ${animateOpen ? styles.open : ''}`}>
        <div className={`${styles.modalContent} ${animateOpen ? styles.open : ''}`}>
          <ViewerHeader
            onBack={handleClose}
            title={item?.title || (isLoadingItem ? 'Loading…' : 'Untitled')}
            isPublished={itemIsPublished}
            canPublish={itemCanPublish}
            canUnpublish={itemCanUnpublish}
            isPublishing={isItemPublishing}
            onPublish={() => setItemPublicationMode('publish')}
            onUnpublish={() => setItemPublicationMode('unpublish')}
            canDelete={itemCanDelete}
            onDelete={() => setIsItemDeleteModalOpen(true)}
            showPresent={presentState.showPresent}
            isPresentActive={presentState.isPresentActive}
            isGeneratingSlides={presentState.isGeneratingSlides}
            slideCountdown={presentState.slideCountdown}
            onTogglePresent={() => presentHandlersRef.current?.togglePresent()}
            showDownloadPptx={presentState.showDownloadPptx}
            isDownloadingPptx={presentState.isDownloadingPptx}
            onDownloadPptx={() => presentHandlersRef.current?.downloadPptx()}
            editorMode="item"
          />
          <div className={styles.viewerBody}>
            {!isLoadingItem && !itemError && item && navSections.length > 1 && (
              <FormNavSidebar
                sections={navSections}
                activeSectionId={activeSectionId}
                onSectionClick={handleSidebarSectionClick}
              />
            )}
            <div className={styles.viewerMainColumn}>
              {isLoadingItem && (
                <div className={styles.loading}>
                  <CircularProgress size={24} />
                  <span>Loading item...</span>
                </div>
              )}
              {itemError && (
                <div className={styles.error}>
                  <ErrorIcon fontSize="large" />
                  <p>{itemError}</p>
                </div>
              )}
              {!itemError && item && iterationLike && (
                <>
                  <HeroSection
                    typeLabel={itemTypePillLabel}
                    title={item?.title || 'Untitled'}
                    isEditingTitle={isEditingItemTitle}
                    editTitle={editItemTitle}
                    isSavingTitle={isSavingItemTitle}
                    titleInputRef={itemTitleInputRef}
                    onEditTitleChange={setEditItemTitle}
                    onStartEditingTitle={handleStartEditingItemTitle}
                    onSaveTitle={handleSaveItemTitle}
                    onCancelEditingTitle={handleCancelEditingItemTitle}
                    canEditTitle={itemCanEditValues}
                    readOnlyTitleHint={itemIsPublished ? 'Item is published' : undefined}
                    description={item?.description || ''}
                    isEditingDescription={isEditingItemDescription}
                    editDescription={editItemDescription}
                    isSavingDescription={isSavingItemDescription}
                    descriptionInputRef={itemDescriptionInputRef}
                    onEditDescriptionChange={setEditItemDescription}
                    onStartEditingDescription={handleStartEditingItemDescription}
                    onSaveDescription={handleSaveItemDescription}
                    onCancelEditingDescription={handleCancelEditingItemDescription}
                    canEditDescription={itemCanEditValues}
                    readOnlyDescriptionHint={itemIsPublished ? 'Item is published' : undefined}
                    heroImageUrl={itemHeroInfo?.url}
                    heroElementId={itemHeroInfo?.elementId}
                    heroFileId={itemHeroInfo?.fileId}
                    heroIteration={iterationLike}
                    heroProjectFqn={''}
                    heroEditorMode="item"
                    heroCapabilities={{ canEditMedia: itemCanEditValues, canUseMediaAI: true }}
                    onHeroIterationUpdated={async () => {
                      if (!itemFqn) return;
                      try { const latest = await getLibraryItem(itemFqn); if (latest) setItem(latest); } catch (_) {}
                    }}
                    isPublished={itemIsPublished}
                    showTags={!!itemSchemaPattern}
                    tagDimensions={itemTagDimensions}
                    tagPattern={item?.tag_pattern ?? EMPTY_TAG_PATTERN}
                    tagConstraints={itemTagConstraints}
                    isTagDisabled={isItemContextDisabled}
                    isSavingTags={isSavingItemTags}
                    tagError={itemTagError}
                    onPersistTags={handlePersistItemTags}
                    tagVariant="compact"
                    hideInactiveHeaderChips={isItemContextDisabled}
                  />
                  <div className={styles.viewerContentScroll} ref={contentScrollRef}>
                    <IterationView
                      iteration={iterationLike}
                      projectFqn={''}
                      creativeLabels={[]}
                      activeJobsCount={0}
                      successJobsCount={0}
                      failedJobsCount={0}
                      onUpdate={() => { }}
                      isActiveIteration={true}
                      iterationNumber={0}
                      onClose={handleClose}
                      hasNextUnseen={false}
                      iterations={[iterationLike]}
                      activeIterationFqn={iterationLike.fqn}
                      displayedIterationFqn={iterationLike.fqn}
                      handleDisplayIteration={() => { }}
                      handleSetActiveIteration={async () => { }}
                      handleCreateIteration={async () => { }}
                      handleDeleteIterationRequest={() => { }}
                      isDeletingIteration={false}
                      creativeType={isItemMode ? itemCreativeType : creativeType}
                      isProjectClosed={!itemCanEditValues}
                      isPublished={itemIsPublished}
                      isPublishing={isItemPublishing}
                      canPublish={itemCanPublish}
                      canUnpublish={itemCanUnpublish}
                      onPublishRequest={() => setItemPublicationMode('publish')}
                      onUnpublishRequest={() => setItemPublicationMode('unpublish')}
                      canDelete={itemCanDelete}
                      onDeleteRequest={() => setIsItemDeleteModalOpen(true)}
                      editorMode={'item'}
                      formsMap={formsFromItem}
                      orderedFormIds={orderedItemFormIds}
                      persistValues={persistItemValues}
                      hideHeader
                      sectionRefs={sectionRefs}
                      presentHandlersRef={presentHandlersRef}
                      onPresentStateChange={setPresentState}
                      scrollContainerRef={contentScrollRef}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        {/* Item lifecycle confirmation modal */}
        <ConfirmationModal
          isOpen={itemPublicationMode !== null}
          onClose={() => setItemPublicationMode(null)}
          onConfirm={async () => {
            if (itemPublicationMode === 'publish') {
              await handlePublishItem();
            } else if (itemPublicationMode === 'unpublish') {
              await handleUnpublishItem();
            }
            setItemPublicationMode(null);
          }}
          title={itemPublicationMode === 'publish' ? 'Publish Item' : 'Unpublish Item'}
          message={itemPublicationMode === 'publish'
            ? 'This will publish the item and make it visible to others.'
            : (isProjectOriginItem
              ? 'Unpublishing will remove this from the Library. The content remains available in the originating project.'
              : 'This will revert to Draft status and appear in your My Items search filter.')}
          confirmText={itemPublicationMode === 'publish' ? 'Publish' : 'Unpublish'}
          cancelText="Cancel"
          isConfirming={isItemPublishing}
          variant="primary"
        />

        {/* Item delete confirmation modal */}
        <ConfirmationModal
          isOpen={isItemDeleteModalOpen}
          onClose={() => setIsItemDeleteModalOpen(false)}
          onConfirm={handleDeleteItem}
          title={'Delete Item'}
          message={'Are you sure you want to delete this item? This action cannot be undone.'}
          confirmText={'Delete'}
          cancelText={'Cancel'}
          isConfirming={isItemDeleting}
          variant="destructive"
        />
      </div>
    );
  }

  // Reuse the pre-computed value for render (displayedIterationForUnseen is set before early returns)
  const displayedIteration = displayedIterationForUnseen;
  const canShowFloatingAIBar = !isItemMode && !!displayedIteration && !isDeepsightsTabActive;

  const isNewlyCreated = creative && iterations.length === 0;

  // When summary_element_id is configured, derive description from the displayed iteration's
  // form values instead of the creative-level description (which only tracks the active iteration).
  const summaryElementId = creative?.summary_element_id;
  const hasSummaryElement = !!summaryElementId;
  const effectiveDescription = (() => {
    if (hasSummaryElement && displayedIteration) {
      const raw = displayedIteration.values?.[summaryElementId!];
      if (typeof raw === 'string' && raw.trim()) return raw.trim();
    }
    return creative?.description || '';
  })();

  return (
    <div className={`${styles.modalOverlay} ${animateOpen ? styles.open : ''}`}>
      <div className={`${styles.modalContent} ${animateOpen ? styles.open : ''}`}>
        <ViewerHeader
          onBack={handleClose}
          title={creative?.name || 'Untitled creative'}
          isPublished={isPublished}
          activeJobsCount={creative?._active_jobs ?? 0}
          successJobsCount={creative?._successful_jobs ?? 0}
          failedJobsCount={creative?._failed_jobs ?? 0}
          jobIndicator={
            creative ? (
              <EditorJobIndicator
                creativeFqn={creative.fqn}
                iterationFqns={iterations.map((iteration) => iteration.fqn)}
                onOpenPanel={handleOpenScopedActivityPanel}
                onOpenPanelUnfiltered={handleOpenUnfilteredActivityPanel}
                onNavigateToResult={handleNavigateToJobResult}
                onOpenJobDetail={setSelectedJobFqn}
                onDismissCompletion={handleDismissJobCompletion}
              />
            ) : undefined
          }
          canPublish={creativeTypeAllowsPublish && canPublish}
          canUnpublish={canUnpublish}
          isPublishing={isPublishing}
          publishActionDisabled={!!publishBlockedReason || isPublishing}
          publishDisabledReason={publishBlockedReason}
          onPublish={handleRequestPublish}
          onUnpublish={handleRequestUnpublish}
          canDelete={!isPublished}
          onDelete={() => setIsDeleteCreativeOpen(true)}
          showPresent={presentState.showPresent}
          isPresentActive={presentState.isPresentActive}
          isGeneratingSlides={presentState.isGeneratingSlides}
          slideCountdown={presentState.slideCountdown}
          onTogglePresent={() => presentHandlersRef.current?.togglePresent()}
          showDownloadPptx={presentState.showDownloadPptx}
          isDownloadingPptx={presentState.isDownloadingPptx}
          onDownloadPptx={() => presentHandlersRef.current?.downloadPptx()}
          showVersionHistory={true}
          iterationNumber={displayedIteration?._iteration_number}
          isActiveIteration={displayedIteration?.fqn === activeIterationFqn}
          onOpenVersionHistory={() => setIsVersionHistoryOpen(true)}
          editorMode="iteration"
        />
        <div className={styles.viewerBody}>
          {/* Left sidebar: form navigation with scroll spy */}
          {!error && creative && navSections.length > 1 && (
            <FormNavSidebar
              sections={navSections}
              activeSectionId={activeSectionId}
              onSectionClick={handleSidebarSectionClick}
            />
          )}

          {/* Main content column: sticky hero + scrollable forms */}
          <div className={styles.viewerMainColumn}>
            {/* Loading State */}
            {(!creative && (isLoadingCreative || (isLoadingIterations && iterations.length === 0))) && (
              <div className={styles.loading}>
                <CircularProgress size={24} />
                <span>Loading creative...</span>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className={styles.error}>
                <ErrorIcon fontSize="large" />
                <p>{error}</p>
                <button className={styles.retryButton} onClick={handleRetry}>
                  Retry
                </button>
              </div>
            )}

            {/* Hero + Content (all scrollable together) */}
            {!error && creative && (
              <>
                <div className={styles.viewerContentScroll} ref={contentScrollRef}>
                  <HeroSection
                  typeLabel={creativeType?.name}
                  title={creative?.name || 'Untitled creative'}
                  isEditingTitle={isEditingCreativeName}
                  editTitle={editCreativeName}
                  isSavingTitle={isSavingCreativeName}
                  titleInputRef={creativeNameInputRef}
                  onEditTitleChange={setEditCreativeName}
                  onStartEditingTitle={handleStartEditingCreativeName}
                  onSaveTitle={handleSaveCreativeName}
                  onCancelEditingTitle={handleCancelEditingCreativeName}
                  canEditTitle={!isReadOnly}
                  readOnlyTitleHint={isPublished ? 'Creative is published' : 'Project is closed'}
                  description={effectiveDescription}
                  isEditingDescription={isEditingCreativeDescription}
                  editDescription={editCreativeDescription}
                  isSavingDescription={isSavingCreativeDescription}
                  descriptionInputRef={creativeDescriptionInputRef}
                  onEditDescriptionChange={setEditCreativeDescription}
                  onStartEditingDescription={handleStartEditingCreativeDescription}
                  onSaveDescription={handleSaveCreativeDescription}
                  onCancelEditingDescription={handleCancelEditingCreativeDescription}
                  canEditDescription={!isReadOnly && !hasSummaryElement}
                  readOnlyDescriptionHint={hasSummaryElement ? 'Driven by form element' : isPublished ? 'Creative is published' : 'Project is closed'}
                  heroImageUrl={iterationHeroInfo?.url}
                  heroElementId={iterationHeroInfo?.elementId}
                  heroFileId={iterationHeroInfo?.fileId}
                  heroIteration={displayedIteration ?? undefined}
                  heroProjectFqn={projectFqn}
                  heroEditorMode="iteration"
                  heroCapabilities={{ canEditMedia: !isReadOnly, canUseMediaAI: true }}
                  onHeroIterationUpdated={() => refreshCreativeData(true)}
                  isPublished={isPublished}
                  showTags={!!effectiveProjectTagPattern || !!(creativeType?.creative_tag_schema_fqn?.length)}
                  tagDimensions={tagDimensions}
                  tagPattern={creative?.tag_pattern ?? EMPTY_TAG_PATTERN}
                  tagConstraints={tagConstraints}
                  isTagDisabled={isContextDisabled}
                  tagError={tagError}
                  onPersistTags={handlePersistCreativeTags}
                  tagVariant="compact"
                  showLabels={true}
                  labels={creative?._labels || []}
                  availableLabels={availableProjectLabels}
                  isReadOnly={isReadOnly}
                  onAddLabel={handleAddLabelToCreative}
                  onRemoveLabel={handleRemoveLabelFromCreative}
                />
                  {displayedIteration ? (
                    <IterationView
                      iteration={displayedIteration}
                      projectFqn={projectFqn}
                      creativeLabels={creative?._labels || []}
                      activeJobsCount={creative?._active_jobs ?? 0}
                      successJobsCount={creative?._successful_jobs ?? 0}
                      failedJobsCount={creative?._failed_jobs ?? 0}
                      onUpdate={() => refreshCreativeData(true)}
                      onRemoveLabel={handleRemoveLabelFromCreative}
                      isActiveIteration={displayedIteration.fqn === activeIterationFqn}
                      iterationNumber={displayedIteration._iteration_number}
                      newIterationAvailable={false}
                      onClose={handleClose}
                      hasNextUnseen={hasNextUnseen}
                      onNextUnseen={handleNextUnseen}
                      iterations={iterations}
                      activeIterationFqn={activeIterationFqn}
                      displayedIterationFqn={displayedIterationFqn}
                      handleDisplayIteration={handleDisplayIteration}
                      handleSetActiveIteration={handleSetActiveIteration}
                      handleCreateIteration={handleCreateIteration}
                      handleDeleteIterationRequest={handleDeleteIterationRequest}
                      isDeletingIteration={isDeletingIteration}
                      creativeType={creativeType}
                      creativeTypeName={creativeType?.name}
                      isProjectClosed={isClosed}
                      isPublished={isPublished}
                      isPublishing={isPublishing}
                      canPublish={creativeTypeAllowsPublish && canPublish}
                      canUnpublish={canUnpublish}
                      onPublishRequest={handleRequestPublish}
                      onUnpublishRequest={handleRequestUnpublish}
                      publishDisabledReason={publishBlockedReason}
                      canDelete={!isPublished}
                      onDeleteRequest={() => setIsDeleteCreativeOpen(true)}
                      controlledActiveTab={controlledActiveTab}
                      scrollToFeedbackTimestamp={scrollToFeedbackTimestamp}
                      scrollToFeedbackId={scrollToFeedbackId}
                      onScrollComplete={handleScrollComplete}
                      onExitDeepsights={() => setControlledActiveTab('details')}
                      scrollContainerRef={isDeepsightsTabActive ? undefined : contentScrollRef}
                      chatSheetHostRef={deepsightsSheetHostRef}
                      hideHeader
                      sectionRefs={sectionRefs}
                      presentHandlersRef={presentHandlersRef}
                      onPresentStateChange={setPresentState}
                    />
                  ) : isNewlyCreated ? (
                    <div className={styles.emptyCreativeState}>
                      <AutorenewIcon fontSize="large" style={{ fontSize: 48, marginBottom: 16 }} />
                      <h3>This is a new creative</h3>
                      <p>To get started, you need to generate the first iteration.</p>
                      <button
                        className={styles.generateButton}
                        onClick={() => {
                          handleClose();
                          toast.info('To generate the first iteration, use the "CREATIVE ACTIONS" dropdown in the CreativeBoard.');
                        }}
                      >
                        Close and return to CreativeBoard
                      </button>
                    </div>
                  ) : (
                    <div className={styles.error}>
                      <p>No iterations found for this creative.</p>
                      <p>Try refreshing or using CREATIVE ACTIONS to generate an iteration.</p>
                    </div>
                  )}
                  {canShowFloatingAIBar && <div className={styles.floatingAIBarSpacer} aria-hidden="true" />}
                </div>
                {canShowFloatingAIBar && (
                  <div className={styles.floatingAIBar}>
                    <div className={styles.floatingAIBarShell}>
                      <button
                        type="button"
                        className={styles.floatingAIPrimaryButton}
                        onClick={() => setControlledActiveTab('deepsights')}
                      >
                        <AutoAwesomeIcon fontSize="small" />
                        <span>Explore</span>
                      </button>
                      {sidebarActionItems && sidebarActionItems.length > 0 && (
                        <>
                          <div className={styles.floatingAIBarDivider} aria-hidden="true" />
                          <div className={styles.floatingAISecondaryList}>
                            {sidebarActionItems.map((action) => (
                              <button
                                key={action.label}
                                type="button"
                                className={styles.floatingAISecondaryButton}
                                title={action.description || action.label}
                                onClick={action.onClick}
                              >
                                {action.logoUrl ? (
                                  <img
                                    src={action.logoUrl}
                                    alt=""
                                    aria-hidden="true"
                                    className={styles.floatingAISecondaryLogo}
                                  />
                                ) : (
                                  <AutoAwesomeIcon className={styles.floatingAISecondaryIcon} />
                                )}
                                <span className={styles.floatingAISecondaryLabel}>{action.label}</span>
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
                <div ref={deepsightsSheetHostRef} className={styles.viewerOverlayHost} />
              </>
            )}
          </div>
        </div>

        {/* Version History Panel (overlay) */}
        {isVersionHistoryOpen && (
          <VersionHistoryPanel
            iterations={iterations}
            activeIterationFqn={activeIterationFqn}
            displayedIterationFqn={displayedIterationFqn}
            isReadOnly={isReadOnly}
            isDeletingIteration={isDeletingIteration}
            locallySeenAt={locallySeenAt}
            onClose={() => setIsVersionHistoryOpen(false)}
            onDisplayIteration={handleDisplayIteration}
            onSetActiveIteration={handleSetActiveIteration}
            onCreateIteration={handleCreateIteration}
            onDeleteIteration={handleDeleteIterationRequest}
          />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isConfirmDeleteOpen}
        onClose={() => {
          setIsConfirmDeleteOpen(false);
          setIterationToDeleteFqn(null);
        }}
        onConfirm={handleConfirmDeleteIteration}
        title="Delete Iteration"
        message="Are you sure you want to delete this iteration? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isConfirming={isDeletingIteration}
        variant="destructive"
      />

      {/* Delete Creative Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteCreativeOpen}
        onClose={() => setIsDeleteCreativeOpen(false)}
        onConfirm={async () => {
          if (!creative) return;
          setIsDeletingCreative(true);
          try {
            await deleteSparkCreatives([creative.fqn]);
            toast.success('Creative deleted');
            setIsDeleteCreativeOpen(false);
            onClose();
            onDataRefreshed?.();
          } catch (error: any) {
            const message = error?.response?.data?.error || (error instanceof Error ? error.message : 'Failed to delete creative');
            toast.error(message);
          } finally {
            setIsDeletingCreative(false);
          }
        }}
        title="Delete Creative"
        message={'Are you sure you want to delete this creative? This action cannot be undone.'}
        confirmText="Delete"
        cancelText="Cancel"
        isConfirming={isDeletingCreative}
        variant="destructive"
      />

      <ConfirmationModal
        isOpen={publicationMode !== null}
        onClose={handleCancelPublication}
        onConfirm={handleConfirmPublication}
        title={publicationMode === 'publish' ? 'Publish to Library' : 'Unpublish Creative'}
        message={publicationMode === 'publish'
          ? 'This will publish to the Library immediately and make the creative read-only. Continue?'
          : 'This will remove the Library item and reopen the creative for editing. Continue?'}
        confirmText={publicationMode === 'publish' ? 'Publish' : 'Unpublish'}
        cancelText="Cancel"
        isConfirming={isPublishing}
        variant="primary"
      />

      {!isItemMode && (
        <>
          <ActivityPanel
            isOpen={isActivityPanelOpen}
            onClose={() => setIsActivityPanelOpen(false)}
            scope={activityPanelScope}
            scopeLabel={creative?.name || null}
            onShowAll={() => setActivityPanelScope(null)}
            navigateToStep={navigateToStep}
            openCreativeEditor={openCreativeEditor}
            resolveIteration={resolveIteration}
            resolveFeedbackIdForJob={resolveFeedbackIdForJob}
            scrollToFeedback={scrollToFeedback}
          />
          <JobDetailModal
            isOpen={selectedJobFqn !== null}
            onClose={() => setSelectedJobFqn(null)}
            jobFqn={selectedJobFqn}
          />
        </>
      )}

      {/* Sidebar Spark Action Modal */}
      {sidebarCurrentAction && projectFqn && (
        <SparkActionModal
          isOpen={isSidebarActionModalOpen}
          onClose={() => setIsSidebarActionModalOpen(false)}
          title={sidebarCurrentAction.formatted_label}
          description={sidebarCurrentAction.formatted_description}
          actionName={sidebarCurrentAction.name}
          projectFqn={projectFqn}
          actionArgs={sidebarActionArgs}
          onSuccess={() => setIsSidebarActionModalOpen(false)}
          resultKind={sidebarCurrentAction.result_kind}
          outputTypeName={sidebarCurrentAction.output_type_fqn
            ? currentSparkFlow?.creative_types.find((creativeType) => creativeType.fqn === sidebarCurrentAction.output_type_fqn)?.plural_name
              || currentSparkFlow?.creative_types.find((creativeType) => creativeType.fqn === sidebarCurrentAction.output_type_fqn)?.name
            : undefined}
          currentTypeName={creativeType?.name
            || (sidebarCurrentAction.input_type_fqn
              ? currentSparkFlow?.creative_types.find((creativeType) => creativeType.fqn === sidebarCurrentAction.input_type_fqn)?.name
              : undefined)}
          isCurrentStep={
            sidebarCurrentAction.result_kind === 'creation' &&
            sidebarCurrentAction.output_type_fqn === creative?._creative_type_fqn
          }
        />
      )}

    </div>
  );
};

export default CreativeEditor;
